import React, { useContext, useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import XMPPError from "@xmpp/error"
import { Profile } from "@/reducers/profilesSlice"
import CountryCodeSelector, { countries } from "./CountryCodeSelector"
import { parseDriverAndMucJids } from "./utils"
import { languages } from "@/lib/languages"
import { useTranslation } from "react-i18next"

interface EditDriverProfileModalProps {
  isOpen: boolean
  onClose: () => void
  driver?: Profile | null
}

export const EditDriverProfileModal: React.FC<EditDriverProfileModalProps> = ({
  isOpen,
  onClose,
  driver,
}) => {
  const { t } = useTranslation()
  const isEditMode = !!driver
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [firstName, setFirstName] = useState(driver?.firstName ?? "")
  const [lastName, setLastName] = useState(driver?.lastName ?? "")
  const [phoneNumberWithoutCode, setPhoneNumberWithoutCode] = useState("")
  const [selectedDialCode, setSelectedDialCode] = useState("+48")
  const [preferredLanguage, setPreferredLanguage] = useState(
    driver?.preferredLanguage ?? "pl",
  )

  const { client } = useContext(XmppContext)

  useEffect(() => {
    if (driver) {
      setFirstName(driver.firstName ?? "")
      setLastName(driver.lastName ?? "")
      setPreferredLanguage(driver.preferredLanguage ?? "pl")

      if (driver.phone) {
        const matchingCountry = countries.find((country) =>
          driver.phone?.startsWith(country.dialCode),
        )
        if (matchingCountry) {
          setSelectedDialCode(matchingCountry.dialCode)
          setPhoneNumberWithoutCode(
            driver.phone.substring(matchingCountry.dialCode.length),
          )
        } else {
          setPhoneNumberWithoutCode(driver.phone)
        }
      } else {
        setPhoneNumberWithoutCode("")
      }
    } else {
      setFirstName("")
      setLastName("")
      setPhoneNumberWithoutCode("")
      setSelectedDialCode("+48")
      setPreferredLanguage("pl")
    }
  }, [driver])

  const handlePhoneNumberChange = (value: string) => {
    const cleanValue = value.replace(/[\s+]/g, "")
    setPhoneNumberWithoutCode(cleanValue)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    const fullPhoneNumber = `${selectedDialCode}${phoneNumberWithoutCode}`

    try {
      const response = await AdHocCommands.addDriver({
        client,
        firstName,
        lastName,
        phoneNumber: fullPhoneNumber,
        plates: "-",
        ...(isEditMode && { userJID: driver.jid }),
        skipMuc: true,
      })

      const { driverJID } = parseDriverAndMucJids(response)

      if (driverJID) {
        await AdHocCommands.setPreferredLanguage({
          client,
          driverJid: driverJID,
          language: preferredLanguage,
        })
      }

      window.appToaster.show({
        message: isEditMode
          ? t("fleet.driverProfile.updated")
          : t("fleet.driverProfile.added"),
        intent: "success",
        timeout: 2000,
      })

      onClose()
    } catch (e) {
      const error = e instanceof XMPPError ? e.text : (e as Error).message

      window.appToaster.show({
        message: error || t("fleet.driverProfile.updateFailed"),
        intent: "danger",
        timeout: 2000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="font-normal sm:max-w-[480px]"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {isEditMode
              ? t("fleet.driverProfile.titleEdit")
              : t("fleet.driverProfile.titleAdd")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                {t("fleet.driverProfile.firstName")}
              </Label>
              <Input
                id="firstName"
                disabled={isSubmitting}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full"
                placeholder={t("fleet.driverProfile.firstNamePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                {t("fleet.driverProfile.lastName")}
              </Label>
              <Input
                id="lastName"
                disabled={isSubmitting}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full"
                placeholder={t("fleet.driverProfile.lastNamePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phoneNumber">
                {t("fleet.driverProfile.phoneNumber")}
              </Label>
              <div className="flex gap-2">
                <CountryCodeSelector
                  dialCode={selectedDialCode}
                  onChange={(country) => setSelectedDialCode(country.dialCode)}
                  disabled={isSubmitting}
                />
                <Input
                  id="phoneNumber"
                  disabled={isSubmitting}
                  value={phoneNumberWithoutCode}
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  className="flex-1"
                  placeholder={t("fleet.driverProfile.phoneNumberPlaceholder")}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("fleet.form.preferredLanguage")}</Label>
              <Select
                disabled={isSubmitting}
                value={preferredLanguage}
                onValueChange={setPreferredLanguage}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("fleet.form.selectLanguage")} />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                      <span className="flex items-center gap-2">
                        <span>{language.flag}</span>
                        <span>{language.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? t("fleet.form.saving") : t("common.save")}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditDriverProfileModal
